import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';

export interface InactivityNudgePopupProps {
  open: boolean;
  onContinue: () => void;
  onPauseTimer?: () => void;
  /** When set, show "Pause timer" button and call onPauseTimer when clicked */
  runningCaseId?: number | null;
  /** Optional: show loading state while pausing */
  pauseLoading?: boolean;
}

/**
 * Popup shown when the user has been inactive for a configured period while
 * the timer is running. Informs them that inactivity is tracked and offers
 * "Continue" (I'm back) or "Pause timer" (stop the running case timer).
 */
const InactivityNudgePopup: React.FC<InactivityNudgePopupProps> = ({
  open,
  onContinue,
  onPauseTimer,
  runningCaseId,
  pauseLoading = false,
}) => {
  const showPauseButton = Boolean(runningCaseId != null && onPauseTimer);

  return (
    <Dialog
      open={open}
      onClose={onContinue}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTimeIcon color="primary" />
        You appear to be inactive
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" variant="body2">
          We're tracking activity to ensure accurate time tracking. If you're still working, click
          <strong> Continue</strong>. If you've stepped away, you can pause the timer.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="contained"
          onClick={onContinue}
          color="primary"
        >
          Continue
        </Button>
        {showPauseButton && (
          <Button
            variant="outlined"
            onClick={onPauseTimer}
            disabled={pauseLoading}
            startIcon={pauseLoading ? null : <PauseCircleOutlineIcon />}
          >
            {pauseLoading ? 'Pausing…' : 'Pause timer'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InactivityNudgePopup;
